import { z } from 'zod';
import type { TFunction } from 'i18next';

import type { ReceiveConfirmContextDto } from '@/entities/receive';

import { CONFIRM_ARRIVAL_TEXT_MAX_LENGTH } from './constants';

const lineSchema = z.object({
  lineId: z.string(),
  goodQty: z.number(),
  damagedQty: z.number(),
  lostQty: z.number()
});

/**
 * Mirrors the backend's payload validation so the operator is told what is wrong
 * before a round trip. Deliberately **rejects** rather than clamps, exactly like
 * the API: the JSF page silently redistributed overflow between the three
 * inputs, which is precisely the behaviour the v2 endpoint set out to remove.
 *
 * The schema is rebuilt whenever the context changes, because every ceiling
 * (per-line expectations, the date window, whether losses are legal at all)
 * comes from it.
 */
export const getConfirmArrivalSchema = (t: TFunction, context?: ReceiveConfirmContextDto) => {
  const maxText = (field: string) =>
    z.string().max(
      CONFIRM_ARRIVAL_TEXT_MAX_LENGTH,
      t('receive.confirm.error.maxLength', '{{field}} must be at most {{max}} characters', {
        field,
        max: CONFIRM_ARRIVAL_TEXT_MAX_LENGTH
      })
    );

  return z
    .object({
      targetLocationId: z.string().min(1, t('validation.required', 'Field is required')),
      targetLocationSpaceSeq: z.number().nullable(),
      actualDateOfReceipt: z.string().min(1, t('validation.required', 'Field is required')),
      comments: maxText(t('receive.comments.label', 'Comments')),
      transportDocNumber: maxText(t('receive.document.label', 'Transport Documentation Number')),
      explanationOfLosses: maxText(t('receive.loss.comments.label', 'Explanation of Losses')),
      printGoodsReceivedNote: z.boolean(),
      lines: z.array(lineSchema)
    })
    .superRefine((values, ctx) => {
      if (!context) return;

      if (context.storageAreaRequired && values.targetLocationSpaceSeq == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targetLocationSpaceSeq'],
          message: t('validation.required', 'Field is required')
        });
      }

      // Dates are `yyyy-MM-dd`, so string comparison is chronological.
      if (values.actualDateOfReceipt > context.maxReceiveDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['actualDateOfReceipt'],
          message: t('receive.receivedateinfuture.message', 'Receive date is in the future')
        });
      } else if (values.actualDateOfReceipt < context.minReceiveDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['actualDateOfReceipt'],
          message: t('receive.receivedatebeforedispatch.message', 'Receive date is before actual dispatch date')
        });
      }

      const linesById = new Map(context.lines.map((line) => [line.lineId, line]));

      values.lines.forEach((entered, index) => {
        const line = linesById.get(entered.lineId);
        if (!line) return;

        (['goodQty', 'damagedQty', 'lostQty'] as const).forEach((field) => {
          const value = entered[field];
          if (Number.isInteger(value) && value >= 0) return;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines', index, field],
            message: t('receive.confirm.error.nonNegativeInteger', 'Enter a whole number of 0 or more')
          });
        });

        if (entered.goodQty > line.expectedGoodQty) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines', index, 'goodQty'],
            message: t('receive.confirm.error.aboveExpectedGood', 'Above the expected good quantity ({{expected}})', {
              expected: line.expectedGoodQty
            })
          });
        }

        // Customer deliveries: the backend rejects any loss outright, and the
        // column is hidden, so a non-zero value here can only be stale state.
        if (!context.lostQtyAllowed && entered.lostQty > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines', index, 'lostQty'],
            message: t('receive.confirm.error.lossNotAllowed', 'Losses cannot be recorded on a customer delivery')
          });
        }

        const expectedTotal = line.expectedGoodQty + line.expectedDamagedQty;
        const enteredTotal = entered.goodQty + entered.damagedQty + entered.lostQty;
        if (enteredTotal > expectedTotal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines', index, 'goodQty'],
            message: t(
              'receive.confirm.error.aboveExpectedTotal',
              'Good, damaged and lost together exceed the expected quantity ({{expected}})',
              { expected: expectedTotal }
            )
          });
        }
      });

      const hasLoss = values.lines.some((line) => line.lostQty > 0);
      if (hasLoss && values.explanationOfLosses.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['explanationOfLosses'],
          message: t('validation.required', 'Field is required')
        });
      }
    });
};

export type ConfirmArrivalSchema = ReturnType<typeof getConfirmArrivalSchema>;
